import {
  Button,
  Caption,
  Headline,
  IconButton,
  Separator,
  Title,
} from '@yaradigitallabs/ahua-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BackfillTable } from './BackfillTable';
import routes from '@/app/routes.ts';
import { BackfillForm } from './BackfillForm';
import { useEffect } from 'react';
import {
  getBackfillExecutions,
  postBackfillData,
} from '@/features/backfill/store/backfill.thunk.ts';
import { formatDateToISOString } from '@/utils/helpers.ts';
import { paginationValues } from '@/features/tags/utils/constants.ts';
import Pagination from '@/features/tags/component/TagsPagination.tsx';
import { useAppDispatch, useAppSelector } from '@/app/hooks.ts';
import { useNotification } from '@/components/notification/useNotification.tsx';

export const BackFill = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const notification = useNotification();
  const softTagData = location.state?.softTagData;
  const { loading, pagination } = useAppSelector(state => state.backfill);

  useEffect(() => {
    if (softTagData) {
      dispatch(getBackfillExecutions({ soft_tag_id: softTagData.id }));
    } else {
      navigate(routes.LIST_TAGS);
    }
  }, [dispatch, softTagData, navigate]);

  if (!softTagData) {
    return null; // Render nothing while redirecting
  }

  const handlePaginationChange = (paginationData: {
    size: number;
    page: number;
  }) => {
    dispatch(
      getBackfillExecutions({
        soft_tag_id: softTagData.id,
        page: paginationData.page,
        limit: paginationData.size,
      })
    );
  };

  const handleBackfill = (values: {
    startTime: string;
    endTime: string;
    batchSize?: number;
    retry?: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    extraFields?: Record<string, any>;
  }) => {
    const formattedStartTime = formatDateToISOString(values.startTime);
    const formattedEndTime = formatDateToISOString(values.endTime);
    const batchSize = values.batchSize;
    const retry = values.retry;
    dispatch(
      postBackfillData({
        soft_tag_id: softTagData.id,
        startTime: formattedStartTime,
        endTime: formattedEndTime,
        ...(batchSize != null && { batchSize: batchSize }),
        ...(retry != null && { retryCount: retry }),
        ...(values.extraFields &&
          Object.keys(values.extraFields).length > 0 && {
            extraFields: values.extraFields,
          }),
      })
    )
      .unwrap()
      .then(() => {
        notification('Backfilling request successfully sent', 'success');
        dispatch(getBackfillExecutions({ soft_tag_id: softTagData.id }));
      })
      .catch(err => {
        const error = err?.response?.data.error;
        notification(error, 'destructive');
      });
  };

  return (
    <div className="w-full">
      <div className="flex flex-row py-1 px-8 gap-2 items-center">
        <IconButton
          className="p-0"
          size="xs"
          icon="ArrowLeft"
          variant="ghost"
          onClick={() => navigate(routes.LIST_TAGS)}
        />
        <Headline size="2xs">Back to soft tags search</Headline>
      </div>
      <Separator
        height="100%"
        mode="light"
        orientation="horizontal"
        width="100%"
      />
      <div className="px-4 py-2">
        <Title size="n">{`Backfill Soft Tag`}</Title>
        <Caption size="m">
          {`${softTagData?.name + '|'}${softTagData?.description || ''}`}
        </Caption>
      </div>
      <div className="flex flex-row gap-8 mt-6 px-4">
        <div className="w-1/2">
          <BackfillForm onSubmit={handleBackfill} />
        </div>
        <div className="w-1/2">
          <div className="flex justify-end mb-2">
            <Button
              className="p-0"
              label="Refresh"
              size="xs"
              variant="outline"
              disabled={loading}
              onClick={() =>
                handlePaginationChange({
                  page: pagination.page,
                  size: pagination.size,
                })
              }
            />
          </div>
          <BackfillTable handlePaginationChange={handlePaginationChange} />
          <Pagination
            loading={loading}
            dataCount={pagination.count}
            limitOptions={paginationValues}
            paginatedData={{ page: pagination.page, size: pagination.size }}
            setPaginatedData={handlePaginationChange}
          />
        </div>
      </div>
    </div>
  );
};

export default BackFill;
